import {
  AccessorFn,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  RowSelectionState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { ReactElement } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import styles from './Table.module.css';

import { Checkbox } from '@/shared/components/atoms/Checkbox';
import Pagination from '@/shared/components/atoms/Pagination';
import { ITableProps } from '@/shared/components/atoms/Table/ITable';
import { Typography } from '@/shared/components/atoms/Typography';
import { clsxm } from '@/shared/lib';

/**
 
 * @param data : array of table data
 * @param columns : array of columns object should contain accessorKey , column object can contain header,custom header or custom cell 
 * @param selection: boolean if true ? render checkbox and its functionality
 * @param pagination: boolean if true ? render pagination and you should IPagination props are required and its functionality
 * @param getIsSelectAll: fun we call this function with parameter true when user checked select all 
 * @param getSelectedRows: fun we call this function with selected rows data
 * @param returnSpecificRowData: fun if u don't need to return all row data in getSelectedRows ? use this function we pass row data and u return Partial of it or anything 
 * @param tableRef : we are using this to asseign table methods for it
 * @param rowContainerClassNameFu : any calssName end with (Fu) this is custom style depend on a value by take current value and return utlits stles
 * @param rowCellClassNameFu : any calssName end with (Fu) this is custom style depend on a value by take current value and return utlits stles
 * 
 * @param columnsDividerIndexes : array of indexes of columns divider start from (1)
 * @param stickyFirstCloumn : if you need to make first column fixed not scrollable 
 * @param stickyLastCloumn : if you need to make last column fixed not scrollable 
 * @param stickyNCloumns : if you need to sticky number of columns (from start) 
 * @param tableWrapperClassName : you can use it to customize table hieght
 * @param renderComponentBeforHead : if u need to render component before header 
 * @returns table
 */

const TableComponent = function Table<T>({
  data,
  columns,
  selection = true,
  pagination,
  noDataMessage,
  onPageChange,
  onClickRow,
  currentPage,
  perPage,
  totalCount,
  getIsSelectAll,
  getSelectedRows,
  returnSpecificRowData,
  containerClassName,
  tableWrapperClassName,
  tableClassName,
  headCellClassName,
  headerClassName,
  rowCellClassName,
  headContainerClassName,
  rowContainerClassName,
  rowContainerClassNameFu,
  rowCellClassNameFu,
  footerClassName,
  paginationWrapperClassName,
  tableRef,
  columnsDividerIndexes,
  stickyFirstCloumn,
  stickyLastCloumn,
  stickyNCloumns,
  renderComponentBeforHead,
  footerContent,
  infiniteScrollProps,
  columnVisibility,
}: ITableProps<T>): ReactElement {
  const {
    t,
    i18n: { dir },
  } = useTranslation();

  /********************************************************************************* 
   *                            style classname
   /****************************************************************************** */
  const containerStyle = clsx(styles['container'], containerClassName || '');
  // scrollbar style and table wrapper
  const tableWrapperStyle = clsx(
    tableWrapperClassName,
    tableWrapperClassName?.includes('whitespace') ? '' : styles['nowrap'],
    styles['tableWrapper'],
    'scrollbar-thumb-background-disable scrollbar-track-background-100 scrollbar-thumb-rounded-lg scrollbar-track-rounded-lg scrollbar-thin  scrollbar-h-5 scrollbar-w-5'
  );

  const tableStyle = clsx(styles['table'], tableClassName || '');

  const headerStyle = clsx(styles['thead'], headerClassName || '');

  const headContainerStyle = clsx(
    styles['headContainer'],
    headContainerClassName || ''
  );
  const rowContainerStyle = clsx('bg-white', rowContainerClassName || '');

  const headCellStyle = clsx(
    styles['headCell'],
    headCellClassName || '',
    stickyFirstCloumn && styles['stickyFirstCloumn'],
    stickyLastCloumn && styles['stickyLastCloumn']
  );
  const rowCellStyle = clsx(
    styles['rowCell'],
    rowCellClassName || '',
    stickyFirstCloumn && styles['stickyFirstCloumn'],
    stickyLastCloumn && styles['stickyLastCloumn']
  );

  const paginationWrapperStyle = clsx(
    styles['paginationWrapper'],
    paginationWrapperClassName || ''
  );

  /********************************************************************************* 
     *                            state 
     /****************************************************************************** */
  // serilaze columns divider to be object its attributes will be indexes value and value will be style for divider
  const columnsDividerStylesObj = React.useRef<{ [k: number]: string }>({});
  React.useMemo(
    () =>
      columnsDividerIndexes?.forEach(
        (item) =>
          (columnsDividerStylesObj.current[item] = `${
            dir() === 'rtl' ? 'border-l-xs' : 'border-r-xs'
          }  border-background-200`)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsDividerIndexes]
  );

  // serialize selected rows per page
  const allPagesRowsSelectionRef = React.useRef({
    [`${currentPage}`]: {
      pageRowsSelection: [] as unknown as RowSelectionState,
      pageRowsSelectionData: [] as T[],
    },
  });

  const [pageRowsSelection, setPageRowsSelection] = React.useState({});

  const columnHelper = createColumnHelper<AccessorFn<T>>();
  // if slection ture reshape columns and inject selection checkbox and functionality to first columns
  const newColumns = () => {
    if (!selection) return columns;
    return [
      columnHelper.accessor(columns[0].accessorKey, {
        header: ({ table }) => (
          <span className='flex items-center gap-2'>
            <Checkbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
            {/* if column object contain custom header attribute (not string) dispaly it if not (string) dispaly default typography */}
            <>
              {typeof columns[0]?.header === 'string' ? (
                <Typography variant='subtitle1'>
                  {columns[0].header || columns[0].accessorKey}
                </Typography>
              ) : (
                columns[0]?.header({ table })
              )}
            </>
          </span>
        ),
        cell: ({ row, getValue }) => (
          <span className='flex items-center gap-2'>
            <Checkbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
                onClick: (e) => e.stopPropagation(),
              }}
            />
            {/* if column object contain cell attribute dispaly it if not dispaly default shape value */}
            <>
              {columns[0]?.cell ? (
                columns[0]?.cell({ row, getValue })
              ) : (
                <>{getValue()}</>
              )}
            </>
          </span>
        ),
      }),

      ...columns.slice(1),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns: newColumns(),
    state: {
      rowSelection: pageRowsSelection,
      columnVisibility: columnVisibility as VisibilityState,
      expanded,
    },
    onRowSelectionChange: (state) => {
      setPageRowsSelection(state);
    },
    onExpandedChange: setExpanded,
    //  TODO: remove any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getSubRows: (row: any) => row?.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });
  // assign ref to table to be used from parent
  if (tableRef) tableRef.current = table;

  // set selected rows state  & if select all state to parent
  // if u need to return specific attributes from row not all row data use returnSpecificRowData
  React.useEffect(() => {
    // update parent when select all checked
    if (getIsSelectAll && selection)
      getIsSelectAll(table.getIsAllRowsSelected());

    // update parent with all selected data
    if (selection && getSelectedRows) {
      const pageRowsSelectionData = table
        .getSelectedRowModel()
        .flatRows.map((row) =>
          returnSpecificRowData
            ? returnSpecificRowData(row.original)
            : row.original
        );
      // update selection all table state(in all pages )
      allPagesRowsSelectionRef.current = {
        ...allPagesRowsSelectionRef.current,
        [`${currentPage}`]: {
          pageRowsSelection,
          pageRowsSelectionData: pageRowsSelectionData,
        },
      };

      // set parent state with selected data and flat it to be one array
      getSelectedRows(
        Object.values(allPagesRowsSelectionRef.current)
          .map((data) => data?.pageRowsSelectionData)
          .flat()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRowsSelection]);

  /*  determine if footers are in column definition
      INFO: https://github.com/TanStack/table/discussions/5023
  */
  const hasFooters =
    table
      .getFooterGroups()
      .map((group) =>
        group.headers.map((header) => header.column.columnDef.footer)
      )
      .flat()
      .filter(Boolean).length > 0;

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return infiniteScrollProps ? (
      <InfiniteScroll useWindow={false} {...infiniteScrollProps}>
        {children}
      </InfiniteScroll>
    ) : (
      <>{children}</>
    );
  };

  // console.log(table?.getRowModel()?.rows[0].getValue("name"))

  return (
    <div className={containerStyle}>
      {/* make dir ltr to put scroll in right side then make content dir as choosen */}
      <div dir='ltr' className={tableWrapperStyle}>
        <Wrapper>
          <table dir={dir()} className={tableStyle}>
            <thead className={headerStyle}>
              {renderComponentBeforHead && (
                <tr>
                  <th colSpan={table?.getAllColumns().length}>
                    {renderComponentBeforHead}
                  </th>
                </tr>
              )}
              {table?.getHeaderGroups()?.map((headerGroup) => (
                <tr className={headContainerStyle} key={headerGroup?.id}>
                  {headerGroup?.headers?.map((header, index) => (
                    <th
                      colSpan={header.colSpan}
                      // if there are grouping get the style of current cell
                      className={clsx(
                        headCellStyle,
                        columnsDividerStylesObj?.current[index + 1],
                        stickyNCloumns &&
                          index < stickyNCloumns &&
                          `sticky z-10  bg-background-inherit`
                      )}
                      // TODO : Need to refactore fixed right position
                      style={{
                        right: index < (stickyNCloumns || 0) ? index * 150 : 0,
                        left: index < (stickyNCloumns || 0) ? index * 150 : 0,
                      }}
                      key={header.id}
                    >
                      <Typography variant='subtitle1'>
                        {header?.isPlaceholder
                          ? null
                          : flexRender(
                              header?.column?.columnDef?.header,
                              header?.getContext()
                            )}
                      </Typography>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {/* if there are no data */}
              {table?.getRowModel()?.rows.length === 0 && (
                <tr className='text-center'>
                  <td className='pt-32' colSpan={columns.length}>
                    {noDataMessage || t('no-data')}
                  </td>
                </tr>
              )}
              {table?.getRowModel()?.rows?.map((row) => (
                <tr
                  onClick={() => onClickRow && onClickRow(row.original)}
                  // mix default style with custom design that depend on some values in rows
                  className={clsxm(
                    rowContainerStyle,
                    rowContainerClassNameFu &&
                      rowContainerClassNameFu(row.original)
                  )}
                  key={row?.id}
                >
                  {row?.getVisibleCells()?.map((cell, index) => (
                    <td
                      title={
                        !cell?.getValue() ||
                        typeof cell?.getValue() === 'object'
                          ? ''
                          : String(cell?.getValue())
                      }
                      // if there are grouping get the style of current cell
                      className={clsx(
                        rowCellStyle,
                        rowCellClassNameFu && rowCellClassNameFu(row.original),
                        columnsDividerStylesObj?.current[index + 1],
                        stickyNCloumns &&
                          index < stickyNCloumns &&
                          `sticky z-auto  bg-background-inherit`
                      )}
                      // TODO : Need to refactore fixed right position
                      style={{
                        right: index < (stickyNCloumns || 0) ? index * 150 : 0,
                        left: index < (stickyNCloumns || 0) ? index * 150 : 0,
                      }}
                      key={cell?.id}
                    >
                      <Typography variant='subtitle2'>
                        {flexRender(
                          cell?.column?.columnDef?.cell,
                          cell?.getContext()
                        )}
                      </Typography>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className={footerClassName}>
              {footerContent ??
                (hasFooters &&
                  table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <th
                          className={clsx(rowCellStyle, 'text-start')}
                          key={header.id}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.footer,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  )))}
            </tfoot>
          </table>
        </Wrapper>
      </div>
      {pagination && (
        <div className={paginationWrapperStyle}>
          <Pagination
            onPageChange={(page) => {
              // set previous page selection if found else reset selection for this page
              if (allPagesRowsSelectionRef.current[page]?.pageRowsSelection)
                table?.setRowSelection(
                  allPagesRowsSelectionRef.current[page]?.pageRowsSelection
                );
              else table?.resetRowSelection();
              onPageChange(page);
            }}
            currentPage={currentPage}
            totalCount={totalCount}
            perPage={perPage}
          />
        </div>
      )}
    </div>
  );
};

const MemoizedTable = React.memo(TableComponent) as typeof TableComponent;
export default MemoizedTable;
